<template>
  <section class="thematic-associations">
    <div class="thematic-heading">
      <h3>Campings et hébergements associés</h3>
      <p class="text--secondary mb-0">
        Dépliez un camping, puis ajoutez les hébergements de votre choix à la sélection.
      </p>
    </div>

    <div class="thematic-panes">
      <v-card
        outlined
        class="thematic-pane"
      >
        <div class="thematic-pane-heading">
          <div class="thematic-pane-title">
            <v-icon
              color="primary"
              size="20"
            >
              {{ icons.mdiFormatListBulleted }}
            </v-icon>
            <h4>Campings et hébergements disponibles</h4>
          </div>
        </div>
        <div class="thematic-search">
          <v-text-field
            v-model="campingSearch"
            :prepend-inner-icon="icons.mdiMagnify"
            label="Rechercher un camping"
            placeholder="Nom ou identifiant du camping"
            clearable
            outlined
            dense
            hide-details
            @keydown.enter.prevent
          ></v-text-field>
        </div>

        <div
          class="thematic-pane-list"
          aria-label="Campings et hébergements disponibles"
          tabindex="0"
        >
          <v-progress-linear
            v-if="loadingCampings"
            indeterminate
            color="primary"
          ></v-progress-linear>
          <v-alert
            v-if="campingsError"
            dense
            text
            type="error"
            class="ma-3"
          >
            {{ campingsError }}
            <v-btn
              small
              text
              type="button"
              :disabled="loadingCampings"
              @click="loadCampings"
            >
              Réessayer
            </v-btn>
          </v-alert>
          <div
            v-if="!loadingCampings && !campingsError && !availableCampings.length"
            class="thematic-empty text--secondary"
          >
            <v-icon
              size="32"
              class="mb-3"
            >
              {{ icons.mdiMagnify }}
            </v-icon>
            <p class="mb-0">
              Aucun camping ne correspond à votre recherche.
            </p>
          </div>
          <ul class="thematic-tree">
            <li
              v-for="camping in availableCampings"
              :key="camping.id"
              class="thematic-camping"
            >
              <div class="thematic-camping-row">
                <button
                  type="button"
                  class="thematic-toggle"
                  :aria-expanded="expandedCampings.includes(camping.id) ? 'true' : 'false'"
                  @click="toggleCamping(camping.id)"
                >
                  <v-icon size="18">
                    {{ expandedCampings.includes(camping.id) ? icons.mdiChevronDown : icons.mdiChevronRight }}
                  </v-icon>
                  <v-icon
                    color="primary"
                    size="20"
                  >
                    {{ icons.mdiTent }}
                  </v-icon>
                  <span class="thematic-row-label">
                    <strong>{{ camping.name }}</strong>
                    <span
                      v-if="camping.secureHolidayId"
                      class="thematic-meta text--secondary"
                    >Camping n° {{ camping.secureHolidayId }}</span>
                  </span>
                </button>
                <v-btn
                  icon
                  small
                  type="button"
                  color="primary"
                  :disabled="disabled || selectedCampingIds.includes(camping.id)"
                  :title="selectedCampingIds.includes(camping.id) ? 'Camping déjà associé' : 'Associer le camping seul'"
                  :aria-label="(selectedCampingIds.includes(camping.id) ? 'Camping déjà associé : ' : 'Associer le camping seul : ') + camping.name"
                  @click="addCamping(camping.id)"
                >
                  <v-icon size="21">
                    {{ selectedCampingIds.includes(camping.id) ? icons.mdiCheckCircleOutline : icons.mdiPlusCircleOutline }}
                  </v-icon>
                </v-btn>
              </div>
              <div
                v-if="expandedCampings.includes(camping.id)"
                class="thematic-children"
              >
                <v-progress-linear
                  v-if="loadingAccommodations[camping.id]"
                  indeterminate
                  color="primary"
                ></v-progress-linear>
                <v-alert
                  v-if="accommodationErrors[camping.id]"
                  dense
                  text
                  type="error"
                  class="my-2"
                >
                  {{ accommodationErrors[camping.id] }}
                  <v-btn
                    small
                    text
                    type="button"
                    :disabled="loadingAccommodations[camping.id]"
                    @click="loadAccommodations(camping.id)"
                  >
                    Réessayer
                  </v-btn>
                </v-alert>
                <div
                  v-if="accommodations[camping.id] && accommodations[camping.id].length"
                  class="thematic-child-tools"
                >
                  <v-text-field
                    v-model="accommodationSearches[camping.id]"
                    :prepend-inner-icon="icons.mdiMagnify"
                    label="Rechercher un hébergement"
                    clearable
                    outlined
                    dense
                    hide-details
                    @keydown.enter.prevent
                  ></v-text-field>
                  <v-btn
                    small
                    text
                    type="button"
                    color="primary"
                    class="mt-1"
                    :disabled="disabled || allAccommodationsSelected(camping.id)"
                    :aria-label="'Associer tous les hébergements de ' + camping.name"
                    @click="selectAllAccommodations(camping.id)"
                  >
                    <v-icon
                      left
                      size="16"
                    >
                      {{ icons.mdiPlus }}
                    </v-icon>
                    Tous les hébergements
                  </v-btn>
                </div>
                <ul class="thematic-tree">
                  <li
                    v-for="accommodation in availableAccommodationItems(camping.id)"
                    :key="accommodation.id"
                    class="thematic-accommodation-row"
                  >
                    <v-icon
                      size="18"
                      :color="isAccommodationSelected(accommodation.id) ? 'secondary' : 'primary'"
                    >
                      {{ icons.mdiBedOutline }}
                    </v-icon>
                    <span
                      class="thematic-row-label"
                      :class="{ 'text--secondary': isAccommodationSelected(accommodation.id) }"
                    >{{ accommodation.name }}</span>
                    <v-btn
                      icon
                      small
                      type="button"
                      color="primary"
                      :disabled="disabled || isAccommodationSelected(accommodation.id)"
                      :title="isAccommodationSelected(accommodation.id) ? 'Hébergement déjà associé' : 'Associer cet hébergement'"
                      :aria-label="(isAccommodationSelected(accommodation.id) ? 'Hébergement déjà associé : ' : 'Associer l’hébergement : ') + accommodation.name"
                      @click="addAccommodation(camping.id, accommodation.id)"
                    >
                      <v-icon size="20">
                        {{ isAccommodationSelected(accommodation.id) ? icons.mdiCheckCircleOutline : icons.mdiPlusCircleOutline }}
                      </v-icon>
                    </v-btn>
                  </li>
                </ul>
                <p
                  v-if="accommodations[camping.id] && !availableAccommodationItems(camping.id).length"
                  class="thematic-group-empty text--secondary"
                >
                  {{ accommodations[camping.id].length ? 'Aucun hébergement ne correspond à votre recherche.' : 'Aucun hébergement disponible pour ce camping.' }}
                </p>
              </div>
            </li>
          </ul>
        </div>
        <div class="thematic-pane-footer text--secondary">
          {{ availableCampings.length }} camping(s) affiché(s)
          <span>Le bouton + du camping associe le camping seul.</span>
        </div>
      </v-card>

      <v-card
        outlined
        class="thematic-pane"
      >
        <div class="thematic-pane-heading">
          <div class="thematic-pane-title">
            <v-icon
              color="primary"
              size="20"
            >
              {{ icons.mdiCheckCircleOutline }}
            </v-icon>
            <h4>Votre sélection</h4>
          </div>
          <v-btn
            small
            text
            type="button"
            color="error"
            :disabled="disabled || !selectedCampingIds.length"
            @click="clearSelection"
          >
            <v-icon
              left
              size="16"
            >
              {{ icons.mdiClose }}
            </v-icon>
            Tout retirer
          </v-btn>
        </div>
        <div class="thematic-search">
          <v-text-field
            v-model="selectionSearch"
            :prepend-inner-icon="icons.mdiMagnify"
            label="Rechercher dans la sélection"
            placeholder="Camping ou hébergement"
            clearable
            outlined
            dense
            hide-details
            @keydown.enter.prevent
          ></v-text-field>
        </div>
        <div
          class="thematic-pane-list"
          aria-label="Campings et hébergements associés"
          tabindex="0"
        >
          <div
            v-if="!selectedCampingIds.length"
            class="thematic-empty text--secondary"
          >
            <v-icon
              size="36"
              color="primary"
              class="mb-3"
            >
              {{ icons.mdiPlusCircleOutline }}
            </v-icon>
            <strong class="mb-2">Votre sélection est vide</strong>
            <p class="mb-0">
              Utilisez les boutons + à gauche pour associer un camping ou ses hébergements.
            </p>
          </div>
          <div
            v-else-if="!selectionGroups.length"
            class="thematic-empty text--secondary"
          >
            Aucun élément ne correspond à votre recherche.
          </div>
          <ul class="thematic-tree">
            <li
              v-for="group in selectionGroups"
              :key="group.camping.id"
              class="thematic-camping"
            >
              <div class="thematic-camping-row">
                <button
                  type="button"
                  class="thematic-toggle"
                  :aria-expanded="isSelectionExpanded(group.camping.id) ? 'true' : 'false'"
                  @click="toggleSelectionCamping(group.camping.id)"
                >
                  <v-icon size="18">
                    {{ isSelectionExpanded(group.camping.id) ? icons.mdiChevronDown : icons.mdiChevronRight }}
                  </v-icon>
                  <v-icon
                    color="primary"
                    size="20"
                  >
                    {{ icons.mdiTent }}
                  </v-icon>
                  <span class="thematic-row-label">
                    <strong>{{ group.camping.name }}</strong>
                    <span class="thematic-meta text--secondary">{{ selectedAccommodationIds(group.camping.id).length }} hébergement(s) associé(s)</span>
                  </span>
                </button>
                <v-btn
                  icon
                  small
                  type="button"
                  color="error"
                  :disabled="disabled"
                  title="Retirer le camping et ses hébergements"
                  :aria-label="'Retirer le camping et ses hébergements : ' + group.camping.name"
                  @click="removeCamping(group.camping.id)"
                >
                  <v-icon size="20">
                    {{ icons.mdiClose }}
                  </v-icon>
                </v-btn>
              </div>
              <div
                v-if="isSelectionExpanded(group.camping.id)"
                class="thematic-children"
              >
                <v-progress-linear
                  v-if="loadingAccommodations[group.camping.id]"
                  indeterminate
                  color="primary"
                ></v-progress-linear>
                <v-alert
                  v-if="accommodationErrors[group.camping.id]"
                  dense
                  text
                  type="error"
                  class="my-2"
                >
                  {{ accommodationErrors[group.camping.id] }}
                  <v-btn
                    small
                    text
                    type="button"
                    :disabled="loadingAccommodations[group.camping.id]"
                    @click="loadAccommodations(group.camping.id)"
                  >
                    Réessayer
                  </v-btn>
                </v-alert>
                <ul class="thematic-tree">
                  <li
                    v-for="accommodation in group.accommodations"
                    :key="accommodation.id"
                    class="thematic-accommodation-row"
                  >
                    <v-icon
                      color="success"
                      size="18"
                    >
                      {{ icons.mdiBedOutline }}
                    </v-icon>
                    <span class="thematic-row-label">{{ accommodation.name }}</span>
                    <v-btn
                      icon
                      small
                      type="button"
                      color="error"
                      :disabled="disabled"
                      title="Retirer cet hébergement"
                      :aria-label="'Retirer l’hébergement : ' + accommodation.name"
                      @click="removeAccommodation(group.camping.id, accommodation.id)"
                    >
                      <v-icon size="18">
                        {{ icons.mdiClose }}
                      </v-icon>
                    </v-btn>
                  </li>
                </ul>
                <p
                  v-if="!selectedAccommodationIds(group.camping.id).length"
                  class="thematic-group-empty text--secondary"
                >
                  Camping associé seul. Ajoutez ses hébergements depuis la liste de gauche.
                </p>
              </div>
            </li>
          </ul>
        </div>
        <div
          class="thematic-pane-footer text--secondary"
          aria-live="polite"
        >
          <strong class="primary--text">{{ selectedCampingIds.length }} camping(s) · {{ value.accommodations.length }} hébergement(s) associé(s)</strong>
          <span>Retirer un camping retire aussi ses hébergements.</span>
        </div>
      </v-card>
    </div>
  </section>
</template>

<script>
import { mdiBedOutline, mdiCheckCircleOutline, mdiChevronDown, mdiChevronRight, mdiClose, mdiFormatListBulleted, mdiMagnify, mdiPlus, mdiPlusCircleOutline, mdiTent } from '@mdi/js'
import config from '@/config'

const campingNameCollator = new Intl.Collator('fr', { sensitivity: 'base', numeric: true })

export default {
  props: {
    value: { type: Object, required: true },
    disabled: { type: Boolean, default: false }
  },
  data () {
    return {
      icons: { mdiBedOutline, mdiCheckCircleOutline, mdiChevronDown, mdiChevronRight, mdiClose, mdiFormatListBulleted, mdiMagnify, mdiPlus, mdiPlusCircleOutline, mdiTent },
      campings: [],
      accommodations: {},
      loadingCampings: false,
      loadingAccommodations: {},
      campingsError: null,
      accommodationErrors: {},
      campingSearch: '',
      accommodationSearches: {},
      selectionSearch: '',
      expandedCampings: [],
      collapsedSelectionIds: []
    }
  },
  computed: {
    selectedCampingIds () {
      return this.value.campings.map(camping => camping.id)
    },
    campingItems () {
      return this.withSelectedOptions(this.campings, this.value.campings, 'Camping')
        .sort((first, second) => campingNameCollator.compare(first.name, second.name) || campingNameCollator.compare(first.id, second.id))
    },
    availableCampings () {
      return this.campingItems.filter(camping => this.filterOption(camping, this.campingSearch))
    },
    selectedCampings () {
      const selectedIds = new Set(this.selectedCampingIds)

      return this.campingItems.filter(camping => selectedIds.has(camping.id))
    },
    selectionGroups () {
      return this.selectedCampings.map(camping => {
        const matchesCamping = this.filterOption(camping, this.selectionSearch)
        const selectedIds = this.selectedAccommodationIds(camping.id)
        const accommodations = this.accommodationItems(camping.id).filter(accommodation => selectedIds.includes(accommodation.id))

        return { camping, matchesCamping, accommodations: accommodations.filter(accommodation => matchesCamping || this.filterOption(accommodation, this.selectionSearch)) }
      }).filter(group => group.matchesCamping || group.accommodations.length)
    }
  },
  watch: {
    selectionSearch () {
      if (this.selectionSearch) {
        this.collapsedSelectionIds = []
        this.loadSelectedAccommodations()
      }
    },
    selectedCampingIds: {
      immediate: true,
      handler () { this.loadSelectedAccommodations() }
    }
  },
  created () {
    this.loadCampings()
  },
  methods: {
    requestOptions () {
      return { headers: { Authorization: 'Bearer ' + this.$store.getters.getToken } }
    },
    async loadCampings () {
      if (this.loadingCampings) return
      this.loadingCampings = true
      this.campingsError = null
      try {
        const response = await this.$http.get(config.apiUrl + '/api/thematics/association-options/campings', this.requestOptions())
        this.campings = response.data
      } catch (error) {
        this.campingsError = 'Impossible de charger les campings. Les associations sélectionnées sont conservées.'
      } finally {
        this.loadingCampings = false
      }
    },
    async loadAccommodations (campingId) {
      if (this.loadingAccommodations[campingId] || this.accommodations[campingId]) return
      this.$set(this.loadingAccommodations, campingId, true)
      this.$set(this.accommodationErrors, campingId, null)
      try {
        const response = await this.$http.get(config.apiUrl + '/api/thematics/association-options/campings/' + encodeURIComponent(campingId) + '/accommodations', this.requestOptions())
        this.$set(this.accommodations, campingId, response.data)
      } catch (error) {
        this.$set(this.accommodationErrors, campingId, 'Impossible de charger les hébergements. La sélection est conservée.')
      } finally {
        this.$set(this.loadingAccommodations, campingId, false)
      }
    },
    loadSelectedAccommodations () {
      this.selectedCampingIds
        .filter(id => this.selectedAccommodationIds(id).length && this.isSelectionExpanded(id) && !this.accommodationErrors[id])
        .forEach(id => this.loadAccommodations(id))
    },
    toggleCamping (campingId) {
      if (this.expandedCampings.includes(campingId)) {
        this.expandedCampings = this.expandedCampings.filter(id => id !== campingId)
      } else {
        this.expandedCampings = [...this.expandedCampings, campingId]
        this.loadAccommodations(campingId)
      }
    },
    isSelectionExpanded (campingId) {
      return !this.collapsedSelectionIds.includes(campingId)
    },
    toggleSelectionCamping (campingId) {
      if (this.collapsedSelectionIds.includes(campingId)) {
        this.collapsedSelectionIds = this.collapsedSelectionIds.filter(id => id !== campingId)
        if (this.selectedAccommodationIds(campingId).length) this.loadAccommodations(campingId)
      } else {
        this.collapsedSelectionIds = [...this.collapsedSelectionIds, campingId]
      }
    },
    withSelectedOptions (options, selected, label) {
      const items = options.slice()
      selected.forEach(option => {
        if (!items.some(item => item.id === option.id)) items.push({ ...option, name: option.name || label + ' ' + option.id })
      })

      return items
    },
    filterOption (item, query) {
      const normalize = text => String(text || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('fr').trim()

      return normalize(item.name + ' ' + (item.secureHolidayId || '') + ' ' + item.id).includes(normalize(query))
    },
    selectedAccommodationIds (campingId) {
      return this.value.accommodations.filter(accommodation => accommodation.campingId === campingId).map(accommodation => accommodation.id)
    },
    isAccommodationSelected (accommodationId) {
      return this.value.accommodations.some(accommodation => accommodation.id === accommodationId)
    },
    accommodationItems (campingId) {
      return this.withSelectedOptions(this.accommodations[campingId] || [], this.value.accommodations.filter(accommodation => accommodation.campingId === campingId), 'Hébergement')
    },
    availableAccommodationItems (campingId) {
      return (this.accommodations[campingId] || []).filter(accommodation => this.filterOption(accommodation, this.accommodationSearches[campingId]))
    },
    allAccommodationsSelected (campingId) {
      const accommodations = this.accommodations[campingId] || []

      return accommodations.length > 0 && accommodations.every(accommodation => this.isAccommodationSelected(accommodation.id))
    },
    updateCampings (ids) {
      if (this.disabled) return
      const uniqueIds = [...new Set(ids)]
      this.$emit('input', {
        campings: uniqueIds.map(id => ({ id })),
        accommodations: this.value.accommodations.filter(accommodation => uniqueIds.includes(accommodation.campingId))
      })
    },
    addCamping (campingId) {
      if (this.disabled || this.selectedCampingIds.includes(campingId)) return
      this.collapsedSelectionIds = this.collapsedSelectionIds.filter(id => id !== campingId)
      this.updateCampings([...this.selectedCampingIds, campingId])
    },
    removeCamping (campingId) {
      this.updateCampings(this.selectedCampingIds.filter(id => id !== campingId))
    },
    updateAccommodations (campingId, ids) {
      if (this.disabled) return
      const campings = this.value.campings.slice()
      if (ids.length && !this.selectedCampingIds.includes(campingId)) campings.push({ id: campingId })
      this.$emit('input', {
        campings,
        accommodations: [
          ...this.value.accommodations.filter(accommodation => accommodation.campingId !== campingId),
          ...[...new Set(ids)].map(id => ({ id, campingId }))
        ]
      })
    },
    addAccommodation (campingId, accommodationId) {
      if (this.disabled || this.isAccommodationSelected(accommodationId)) return
      this.collapsedSelectionIds = this.collapsedSelectionIds.filter(id => id !== campingId)
      this.updateAccommodations(campingId, [...this.selectedAccommodationIds(campingId), accommodationId])
    },
    removeAccommodation (campingId, accommodationId) {
      this.updateAccommodations(campingId, this.selectedAccommodationIds(campingId).filter(id => id !== accommodationId))
    },
    selectAllAccommodations (campingId) {
      if (this.disabled || !this.accommodations[campingId] || !this.accommodations[campingId].length) return
      this.collapsedSelectionIds = this.collapsedSelectionIds.filter(id => id !== campingId)
      const ids = new Set([...this.selectedAccommodationIds(campingId), ...this.accommodations[campingId].map(accommodation => accommodation.id)])
      this.updateAccommodations(campingId, [...ids])
    },
    clearSelection () {
      this.updateCampings([])
    }
  }
}
</script>

<style scoped lang="scss">
.thematic-associations {
  min-width: 0;
  .thematic-heading { margin-bottom: 22px; }
  h3 { margin-bottom: 6px; font-size: 18px; font-weight: 600; }
  h4 { font-size: 14px; font-weight: 600; line-height: 1.4; }
  .thematic-panes { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 24px; }
  .thematic-pane { min-width: 0; overflow: hidden; }
  .thematic-pane-heading { display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: 14px 16px; min-height: 62px; }
  .thematic-pane-title { display: flex; align-items: center; gap: 10px; }
  .thematic-search { padding: 0 16px 16px; }
  .thematic-pane-list { height: 520px; overflow-y: auto; overscroll-behavior: contain; border-top: 1px solid rgba(128, 128, 128, .18); padding: 8px; scrollbar-width: thin; }
  .thematic-tree { list-style: none; margin: 0; padding: 0; }
  .thematic-camping { margin-bottom: 4px; }
  .thematic-camping-row { display: flex; align-items: center; gap: 6px; padding: 0 8px 0 0; min-height: 56px; border-radius: 4px; background: rgba(128, 128, 128, .06); }
  .thematic-toggle { display: flex; align-items: center; gap: 7px; text-align: left; padding: 10px 6px; flex: 1; min-width: 0; color: inherit; border-radius: 4px; line-height: 1.4; }
  .thematic-toggle:hover { background: rgba(128, 128, 128, .08); }
  .thematic-toggle:focus-visible, .thematic-pane-list:focus-visible { outline: 2px solid var(--v-primary-base); outline-offset: -2px; }
  .thematic-row-label { min-width: 0; flex: 1; overflow-wrap: anywhere; font-size: 13px; line-height: 1.5; }
  .thematic-meta { display: block; margin-top: 2px; font-size: 11px; font-weight: 400; }
  .thematic-children { margin: 4px 8px 10px 22px; padding-left: 12px; border-left: 1px solid rgba(128, 128, 128, .25); }
  .thematic-child-tools { padding: 12px 0 4px; }
  .thematic-accommodation-row { display: flex; align-items: center; gap: 10px; padding: 6px 4px; min-height: 44px; border-bottom: 1px solid rgba(128, 128, 128, .12); }
  .thematic-accommodation-row:hover { background: rgba(128, 128, 128, .04); }
  .thematic-group-empty { padding: 10px 4px; margin: 0; font-size: 12px; }
  .thematic-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; padding: 48px 24px; min-height: 240px; font-size: 13px; }
  .thematic-pane-footer { display: flex; flex-direction: column; gap: 3px; padding: 12px 16px; border-top: 1px solid rgba(128, 128, 128, .18); font-size: 12px; }
  .thematic-pane-footer span { font-size: 11px; }
  @media (max-width: 800px) {
    .thematic-panes { grid-template-columns: minmax(0, 1fr); gap: 16px; }
    .thematic-pane-list { height: 400px; }
  }
  @media (max-width: 400px) {
    .thematic-pane-heading { flex-wrap: wrap; }
    .thematic-children { margin-left: 14px; padding-left: 8px; }
  }
}
</style>
